import { Component } from '@theme/component';

const STORAGE_PREFIX = 'habelle:favorites:';
const TOGGLE_EVENT = 'habelle:favorite-toggled';

/**
 * @param {string} customerId
 * @returns {string[]}
 */
function getFavorites(customerId) {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_PREFIX + customerId) || '[]');
  } catch {
    return [];
  }
}

/**
 * @param {string} customerId
 * @param {string[]} favorites
 */
function setFavorites(customerId, favorites) {
  localStorage.setItem(STORAGE_PREFIX + customerId, JSON.stringify(favorites));
}

/**
 * @typedef {object} Refs
 * @property {HTMLButtonElement} button
 *
 * @extends {Component<Refs>}
 */
class FavoriteToggle extends Component {
  requiredRefs = ['button'];

  /** @type {(event: CustomEvent) => void} */
  #onDocToggle;

  connectedCallback() {
    super.connectedCallback();
    this.handle = this.dataset.handle || '';
    this.customerId = this.dataset.customerId || '';
    this.loginUrl = this.dataset.loginUrl || '';
    if (!this.handle) return;

    if (this.customerId) this.#sync();
    this.#onDocToggle = (event) => {
      if (event.detail?.handle === this.handle) this.#sync();
    };
    document.addEventListener(TOGGLE_EVENT, this.#onDocToggle);
    this.refs.button.addEventListener('click', this.#onClick);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(TOGGLE_EVENT, this.#onDocToggle);
  }

  #onClick = (event) => {
    event.preventDefault();

    if (!this.customerId) {
      if (this.loginUrl) window.location.href = this.loginUrl;
      return;
    }

    const favorites = getFavorites(this.customerId);
    const isActive = favorites.includes(this.handle);
    const next = isActive ? favorites.filter((h) => h !== this.handle) : [this.handle, ...favorites];

    setFavorites(this.customerId, next);
    this.#sync();
    document.dispatchEvent(new CustomEvent(TOGGLE_EVENT, { detail: { handle: this.handle, active: !isActive } }));
  };

  #sync() {
    const isActive = getFavorites(this.customerId).includes(this.handle);
    this.dataset.active = isActive ? 'true' : 'false';
    this.refs.button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    const label = isActive ? this.dataset.labelRemove : this.dataset.labelAdd;
    if (label) this.refs.button.setAttribute('aria-label', label);
  }
}

if (!customElements.get('favorite-toggle')) {
  customElements.define('favorite-toggle', FavoriteToggle);
}

/**
 * @extends {Component<{}>}
 */
class FavoritesCountBadge extends Component {
  /** @type {() => void} */
  #onToggle;

  connectedCallback() {
    super.connectedCallback();
    this.customerId = this.dataset.customerId || '';
    this.#sync();
    this.#onToggle = () => this.#sync();
    document.addEventListener(TOGGLE_EVENT, this.#onToggle);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(TOGGLE_EVENT, this.#onToggle);
  }

  #sync() {
    const count = this.customerId ? getFavorites(this.customerId).length : 0;
    const countEl = this.querySelector('[data-favorites-count]');
    if (countEl) countEl.textContent = count === 0 ? '' : count > 99 ? '99+' : String(count);
    this.classList.toggle('favorites-count--empty', count === 0);
  }
}

if (!customElements.get('favorites-count-badge')) {
  customElements.define('favorites-count-badge', FavoritesCountBadge);
}

export { TOGGLE_EVENT as FAVORITES_TOGGLE_EVENT, getFavorites };
